import { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { TextContext } from "../context/TextContext";
import {
    deleteClassroom,
    getClassroomsOfSchool,
    getTodayClassroomsOfSchool,
} from "../api/classroomService";
import AddClassroomComponent from "./AddClassroomComponent";
import EditClassroomComponent from "./EditClassroomComponent";
import { AuthContext } from "../auth/AuthContext";
import moment from "moment-jalaali";
import { hasRole } from "../auth/hasRole";

moment.loadPersian({
    dialect: "persian-modern",
    usePersianDigits: true,
});

const ClassRooms = () => {
    const navigate = useNavigate();
    const { logoutUser } = useContext(AuthContext);
    const {
        setSharedText,
        selectedDate,
        setSelectedDate,
        setSelectedClassroomId,
    } = useContext(TextContext);

    const [isLoading, setIsLoading] = useState(true);
    const [classrooms, setClassrooms] = useState([]);
    const [editingClassroom, setEditingClassroom] = useState(null);
    const [hasConnectionError, setHasConnectionError] = useState(false);

    useEffect(() => {
        setSharedText("لیست کلاس‌ها");

        if (selectedDate != null) {
            getClassrooms();
        }
        else
            getTodayClassrooms();

    }, []);

    const getClassrooms = async () => {
        try {
            setIsLoading(true);
            setHasConnectionError(false);

            const data = await getClassroomsOfSchool(selectedDate);

            setClassrooms(data.classrooms || []);
        } catch (err) {
            if (err.response?.status === 401) {
                logoutUser();
                navigate("/login", { replace: true });
                return;
            }

            setHasConnectionError(true);
        } finally {
            setIsLoading(false);
        }
    };

    const getTodayClassrooms = async () => {
        try {
            setIsLoading(true);
            setHasConnectionError(false);

            const data = await getTodayClassroomsOfSchool();

            setClassrooms(data.classrooms || []);
            setSelectedDate(data.date || null);
        } catch (err) {
            if (err.response?.status === 401) {
                logoutUser();
                navigate("/login", { replace: true });
                return;
            }

            setHasConnectionError(true);
        } finally {
            setIsLoading(false);
        }
    };

    const retryGetClassrooms = () => {
        if (selectedDate != null) {
            getClassrooms();
        } else {
            getTodayClassrooms();
        }
    };

    const deleteClassroomHandler = async (classroomId) => {
        try {
            await deleteClassroom(classroomId);

            setClassrooms((current) =>
                current.filter(
                    (classroom) => classroom.id !== classroomId
                )
            );

            toast.success("کلاس با موفقیت حذف شد.");
        } catch (err) {
            toast.error(
                err.response?.data?.message ||
                "حذف کلاس با خطا مواجه شد."
            );
        }
    };

    const goToClassroom = (classroomId) => {
        setSelectedClassroomId(classroomId);
        navigate("/studentsOfClassroom");
    };

    const afterAddClassroom = (id, name) => {
        setClassrooms((current) => [
            ...current,
            {
                id,
                name,
                absenceCount: 0,
                allCount: 0,
                isAbsence: false,
            },
        ]);
    };

    const afterUpdateClassroom = (updatedClassroom) => {
        setClassrooms((current) =>
            current.map((classroom) =>
                classroom.id === updatedClassroom.id
                    ? updatedClassroom
                    : classroom
            )
        );
    };

    const getAbsencePercentage = (classroom) => {
        if (!classroom.allCount) return 0;

        return Math.round(
            (classroom.absenceCount / classroom.allCount) * 100
        );
    };

    const persianFormattedDate = (date) => {
        if (!date) return "";

        const parsedDate = moment(date, "YYYY-MM-DD");
        if (!parsedDate.isValid()) return "";

        return parsedDate.format("jD jMMMM jYYYY");
    };

    const persianDayName = () => {
        return selectedDate ? moment(selectedDate, "YYYY-MM-DD").format("dddd") : ""
    };

    return (
        <div className="classrooms-page" dir="rtl">

            {/* Header */}
            <header className="classrooms-header">

                <div className="header-info">

                    <div className="header-icon">
                        <img src="https://absence.samradco.ir/favicon.ico" className="img-fluid" />
                    </div>

                    <div>
                        <h1 className="fs-6">حضور و غیاب سامراد</h1>
                        <p className="m-0" style={{ fontSize: '13px' }}>{selectedDate == null ? '' : persianDayName(selectedDate) + ", " + persianFormattedDate(selectedDate)}</p>
                    </div>

                </div>

                <div className="d-flex">
                    <button
                        type="button"
                        onClick={() => navigate("/day-absences")}
                        style={{
                            background: "rgba(255,255,255,0.15)",
                            color: "#fff",
                            border: "1px solid rgba(255,255,255,0.3)",
                            borderRadius: "8px",
                            margin: "0 0 0 10px",
                            padding: "7px 16px",
                            fontSize: "13px",
                            fontWeight: "600",
                            cursor: "pointer"
                        }}
                    >
                        غیبت ها
                    </button>

                    <div className="dropdown">
                        <button type="button" className="menu-button text-light border-0 bg-transparent" data-bs-toggle="dropdown" aria-expanded="false" > <svg width="21" height="21" viewBox="0 0 24 24" fill="none" > <circle cx="12" cy="5" r="1.2" fill="currentColor" /> <circle cx="12" cy="12" r="1.2" fill="currentColor" /> <circle cx="12" cy="19" r="1.2" fill="currentColor" /> </svg> </button>

                        <ul className="dropdown-menu dropdown-menu-end">
                            {hasRole("Manager") && <li> <button type="button" className="dropdown-item logout-button text-end" data-bs-toggle="modal" data-bs-target="#AddClassroomModal" >افزودن کلاس</button> </li>}
                            {hasRole("Manager") && <li> <button type="button" className="dropdown-item logout-button text-end" onClick={() => navigate('/users')}>مدیریت کاربران</button> </li>}
                            <li> <button type="button" className="dropdown-item logout-button text-end" onClick={() => navigate('/days')}>تغییر تاریخ</button> </li>
                            <li> <button type="button" className="dropdown-item logout-button text-end" onClick={logoutUser} > خروج از حساب </button> </li>
                        </ul>
                    </div>
                </div>

            </header>

            {/* Content */}
            <main className="classrooms-content">

                {!isLoading && hasConnectionError && (
                    <div className="connection-error-state" role="alert">
                        <p>اتصال اینترنت برقرار نیست.</p>
                        <button type="button" onClick={retryGetClassrooms}>
                            تلاش مجدد
                        </button>
                    </div>
                )}

                {/* Loading */}
                {isLoading && (
                    <div className="loading-state">
                        <div className="spinner-border text-primary" />
                        <span>
                            در حال دریافت کلاس‌ها...
                        </span>
                    </div>
                )}

                {/* Classes */}
                {!isLoading && !hasConnectionError && classrooms.length > 0 && (
                    <div className="classrooms-list">

                        {classrooms.map((classroom) => {

                            const percentage =
                                getAbsencePercentage(classroom);

                            return (
                                <article key={classroom.id} className={"classroom-card " + (classroom.isAbsence ? "has-absence" : "")} onClick={() => goToClassroom(classroom.id)}>

                                    {/* Top */}
                                    <div className="classroom-top">

                                        <div className="classroom-title">

                                            <div className="class-icon">
                                                <svg
                                                    width="21"
                                                    height="21"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                >
                                                    <path
                                                        d="M4 5.5C4 4.67157 4.67157 4 5.5 4H18.5C19.3284 4 20 4.67157 20 5.5V18.5C20 19.3284 19.3284 20 18.5 20H5.5C4.67157 20 4 19.3284 4 18.5V5.5Z"
                                                        stroke="currentColor"
                                                        strokeWidth="1.7"
                                                    />

                                                    <path
                                                        d="M8 8H16"
                                                        stroke="currentColor"
                                                        strokeWidth="1.7"
                                                        strokeLinecap="round"
                                                    />

                                                    <path
                                                        d="M8 12H16"
                                                        stroke="currentColor"
                                                        strokeWidth="1.7"
                                                        strokeLinecap="round"
                                                    />

                                                    <path
                                                        d="M8 16H12"
                                                        stroke="currentColor"
                                                        strokeWidth="1.7"
                                                        strokeLinecap="round"
                                                    />
                                                </svg>
                                            </div>

                                            <div className="classroom-name">
                                                {classroom.name}
                                            </div>

                                        </div>

                                        {hasRole("Manager") &&
                                            <div
                                                className="dropdown"
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                            >

                                                <button
                                                    type="button"
                                                    className="more-button"
                                                    data-bs-toggle="dropdown"
                                                    aria-expanded="false"
                                                >
                                                    <svg
                                                        width="20"
                                                        height="20"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                    >
                                                        <circle
                                                            cx="12"
                                                            cy="5"
                                                            r="1.3"
                                                            fill="currentColor"
                                                        />
                                                        <circle
                                                            cx="12"
                                                            cy="12"
                                                            r="1.3"
                                                            fill="currentColor"
                                                        />
                                                        <circle
                                                            cx="12"
                                                            cy="19"
                                                            r="1.3"
                                                            fill="currentColor"
                                                        />
                                                    </svg>
                                                </button>

                                                <ul className="dropdown-menu dropdown-menu-end">

                                                    <li>
                                                        <button
                                                            type="button"
                                                            className="dropdown-item"
                                                            data-bs-toggle="modal"
                                                            data-bs-target="#EditClassroomModal"
                                                            onClick={() => setEditingClassroom(classroom)}
                                                        >
                                                            <svg
                                                                width="17"
                                                                height="17"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                            >
                                                                <path
                                                                    d="M12 20H21"
                                                                    stroke="currentColor"
                                                                    strokeWidth="1.7"
                                                                    strokeLinecap="round"
                                                                />
                                                                <path
                                                                    d="M16.5 3.5a2.121 2.121 0 0 1 3 3L8 18l-4 1 1-4L16.5 3.5Z"
                                                                    stroke="currentColor"
                                                                    strokeWidth="1.7"
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                />
                                                            </svg>

                                                            <span className="me-2">ویرایش</span>
                                                        </button>
                                                    </li>

                                                    <li>
                                                        <button
                                                            type="button"
                                                            className="dropdown-item delete-item"
                                                            onClick={() =>
                                                                deleteClassroomHandler(
                                                                    classroom.id
                                                                )
                                                            }
                                                        >
                                                            <svg
                                                                width="17"
                                                                height="17"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                            >
                                                                <path
                                                                    d="M4 7H20"
                                                                    stroke="currentColor"
                                                                    strokeWidth="1.7"
                                                                    strokeLinecap="round"
                                                                />
                                                                <path
                                                                    d="M10 11V17"
                                                                    stroke="currentColor"
                                                                    strokeWidth="1.7"
                                                                    strokeLinecap="round"
                                                                />
                                                                <path
                                                                    d="M14 11V17"
                                                                    stroke="currentColor"
                                                                    strokeWidth="1.7"
                                                                    strokeLinecap="round"
                                                                />
                                                                <path
                                                                    d="M6 7L7 20H17L18 7"
                                                                    stroke="currentColor"
                                                                    strokeWidth="1.7"
                                                                    strokeLinejoin="round"
                                                                />
                                                                <path
                                                                    d="M9 7V4H15V7"
                                                                    stroke="currentColor"
                                                                    strokeWidth="1.7"
                                                                />
                                                            </svg>

                                                            حذف کلاس
                                                        </button>
                                                    </li>

                                                </ul>

                                            </div>
                                        }

                                    </div>

                                    {/* Statistics */}
                                    <div className="statistics">

                                        <div className="stat">

                                            <div className="stat-icon absence-icon">
                                                <svg
                                                    width="17"
                                                    height="17"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                >
                                                    <path
                                                        d="M17 19C17 16.7909 15.2091 15 13 15H7C4.79086 15 3 16.7909 3 19V21"
                                                        stroke="currentColor"
                                                        strokeWidth="1.6"
                                                        strokeLinecap="round"
                                                    />

                                                    <circle
                                                        cx="10"
                                                        cy="7"
                                                        r="4"
                                                        stroke="currentColor"
                                                        strokeWidth="1.6"
                                                    />

                                                    <path
                                                        d="M21 21V19C21 17.3431 19.6569 16 18 16"
                                                        stroke="currentColor"
                                                        strokeWidth="1.6"
                                                        strokeLinecap="round"
                                                    />
                                                </svg>
                                            </div>

                                            <div>
                                                <span>
                                                    غیبت
                                                </span>

                                                <strong>
                                                    {Number(classroom.absenceCount || 0).toLocaleString("fa-IR")}
                                                </strong>
                                            </div>

                                        </div>

                                        <div className="stat">

                                            <div className="stat-icon percentage-icon">
                                                <svg
                                                    width="17"
                                                    height="17"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                >
                                                    <path
                                                        d="M7 19L17 5"
                                                        stroke="currentColor"
                                                        strokeWidth="1.7"
                                                        strokeLinecap="round"
                                                    />

                                                    <circle
                                                        cx="7"
                                                        cy="7"
                                                        r="2"
                                                        stroke="currentColor"
                                                        strokeWidth="1.7"
                                                    />

                                                    <circle
                                                        cx="17"
                                                        cy="17"
                                                        r="2"
                                                        stroke="currentColor"
                                                        strokeWidth="1.7"
                                                    />
                                                </svg>
                                            </div>

                                            <div>
                                                <span>
                                                    درصد غیبت
                                                </span>

                                                <strong>
                                                    {percentage.toLocaleString("fa-IR")}٪
                                                </strong>
                                            </div>

                                        </div>

                                    </div>

                                </article>
                            );
                        })}

                    </div>
                )}

                {/* Empty */}
                {!isLoading && !hasConnectionError && classrooms.length === 0 && (
                    <div className="empty-state">

                        <div className="empty-icon">
                            <svg
                                width="32"
                                height="32"
                                viewBox="0 0 24 24"
                                fill="none"
                            >
                                <path
                                    d="M4 5.5C4 4.67157 4.67157 4 5.5 4H18.5C19.3284 4 20 4.67157 20 5.5V18.5C20 19.3284 19.3284 20 18.5 20H5.5C4.67157 20 4 19.3284 4 18.5V5.5Z"
                                    stroke="currentColor"
                                    strokeWidth="1.6"
                                />

                                <path
                                    d="M8 12H16"
                                    stroke="currentColor"
                                    strokeWidth="1.6"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </div>

                        <h3>
                            هنوز کلاسی اضافه نشده
                        </h3>

                        <p>
                            برای شروع، اولین کلاس خود را ایجاد کنید.
                        </p>

                        <button
                            type="button"
                            className="empty-add-button"
                            data-bs-toggle="modal"
                            data-bs-target="#AddClassroomModal"
                        >
                            + افزودن کلاس
                        </button>

                    </div>
                )}

            </main>

            <AddClassroomComponent
                afterAddClassroom={afterAddClassroom}
            />
            <EditClassroomComponent
                classroom={editingClassroom}
                afterUpdateClassroom={afterUpdateClassroom}
            />

            <style>{`

                * {
                    box-sizing: border-box;
                }

                .classrooms-page {
                    min-height: 100vh;
                    background: #f7f8fc;
                    color: #172033;
                }

                /* =========================
                   HEADER
                ========================= */

                .classrooms-header {
                    height: 68px;
                    padding: 0 14px;

                    background: #726be7;
                    border-bottom: 1px solid #edf0f5;
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;

                    position: sticky;
                    top: 0;
                    z-index: 100;
                }

                .header-info {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }

                .header-icon {
                    width: 50px;
                    height: 50px;

                    flex-shrink: 0;

                    border-radius: 12px;

                    color: #2563eb;

                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .header-info h1 {
                    margin: 0;

                    font-size: 15px;
                    font-weight: 800;
                }

                .header-info span {
                    display: block;

                    margin-top: 3px;

                    color: #929aaa;
                    font-size: 10px;
                }

                .add-class-button {
                    height: 40px;

                    border: 1px solid white;
                    border-radius: 11px;

                    padding: 0 12px;

                    background: #2563eb;
                    color: #ffffff;

                    display: flex;
                    align-items: center;
                    justify-content: center;

                    gap: 6px;

                    font-family: inherit;
                    font-size: 11px;
                    font-weight: 700;

                    box-shadow: 0 4px 12px rgba(37, 99, 235, .16);

                    transition: all .18s ease;
                }

                .add-class-button:active {
                    transform: scale(.96);
                }

                /* =========================
                   CONTENT
                ========================= */

                .classrooms-content {
                    width: 100%;
                    max-width: 600px;

                    margin: 0 auto;

                    padding: 14px 12px 30px;
                }

                /* =========================
                   LIST
                ========================= */

                .classrooms-list {
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                }

                /* =========================
                   CARD
                ========================= */

                .classroom-card {
                    background: #ffffff;

                    border: 1px solid #edf0f5;
                    border-radius: 16px;

                    padding: 14px;

                    cursor: pointer;

                    box-shadow:
                        0 2px 9px rgba(20, 35, 60, .025);

                    transition:
                        transform .18s ease,
                        box-shadow .18s ease,
                        border-color .18s ease;

                    -webkit-tap-highlight-color: transparent;
                }

                .classroom-card:active {
                    transform: scale(.985);
                }

                .classroom-card.has-absence {
                    border-right: 3px solid #ef4444;
                }

                /* =========================
                   TOP
                ========================= */

                .classroom-top {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;

                    gap: 8px;
                }

                .classroom-title {
                    min-width: 0;

                    display: flex;
                    align-items: center;
                    gap: 10px;
                }

                .class-icon {
                    width: 43px;
                    height: 43px;

                    flex-shrink: 0;

                    border-radius: 12px;

                    background: #f0f5ff;
                    color: #2563eb;

                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .classroom-name {
                    font-size: 16px;
                    font-weight: 800;

                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .more-button {
                    width: 36px;
                    height: 36px;

                    flex-shrink: 0;

                    border: 0;
                    border-radius: 9px;

                    background: transparent;
                    color: #9aa2b1;

                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .more-button:active {
                    background: #f2f4f7;
                }

                /* =========================
                   STATS
                ========================= */

                .statistics {
                    display: grid;
                    grid-template-columns: 1fr 1fr;

                    gap: 8px;

                    margin-top: 13px;
                }

                .stat {
                    min-width: 0;

                    padding: 9px;

                    background: #f8f9fc;

                    border-radius: 11px;

                    display: flex;
                    align-items: center;

                    gap: 8px;
                }

                .stat-icon {
                    width: 32px;
                    height: 32px;

                    flex-shrink: 0;

                    border-radius: 9px;

                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .absence-icon {
                    background: #fff0f2;
                    color: #e11d48;
                }

                .percentage-icon {
                    background: #eef5ff;
                    color: #2563eb;
                }

                .stat span {
                    display: block;

                    font-size: 9px;
                    color: #929aaa;
                }

                .stat strong {
                    display: block;

                    margin-top: 2px;

                    font-size: 15px;
                    font-weight: 800;

                    color: #273144;
                }

                /* =========================
                   FOOTER
                ========================= */

                .classroom-footer {
                    margin-top: 11px;
                    padding-top: 9px;

                    border-top: 1px solid #f1f3f6;

                    display: flex;
                    align-items: center;
                    justify-content: space-between;

                    color: #929aaa;

                    font-size: 10px;
                }

                .classroom-footer svg {
                    transform: rotate(180deg);
                }

                /* =========================
                   DROPDOWN
                ========================= */

                .dropdown-menu {
                    min-width: 160px;

                    padding: 5px;

                    border: 1px solid #edf0f5;
                    border-radius: 11px;

                    box-shadow:
                        0 8px 25px rgba(20, 35, 60, .10);
                }

                .dropdown-item {
                    border-radius: 8px;

                    padding: 9px 10px;

                    font-family: inherit;
                    font-size: 12px;

                    text-align: right;
                }

                .delete-item {
                    color: #dc3545;

                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .delete-item:hover {
                    background: #fff1f2;
                    color: #dc3545;
                }

                /* =========================
                   LOADING
                ========================= */

                .loading-state {
                    min-height: 60vh;

                    display: flex;
                    flex-direction: column;

                    align-items: center;
                    justify-content: center;

                    gap: 11px;

                    color: #929aaa;

                    font-size: 12px;
                }

                .loading-state .spinner-border {
                    width: 25px;
                    height: 25px;
                }

                .connection-error-state {
                    min-height: 60vh;

                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 12px;

                    text-align: center;
                }

                .connection-error-state p {
                    margin: 0;
                    color: #929aaa;
                    font-size: 15px;
                    font-weight: 700;
                }

                .connection-error-state button {
                    padding: 9px 18px;
                    border: 0;
                    border-radius: 10px;
                    background: #2563eb;
                    color: #fff;
                    font-family: inherit;
                    font-size: 12px;
                    font-weight: 700;
                }

                /* =========================
                   EMPTY
                ========================= */

                .empty-state {
                    min-height: 60vh;

                    display: flex;
                    flex-direction: column;

                    align-items: center;
                    justify-content: center;

                    text-align: center;
                }

                .empty-icon {
                    width: 72px;
                    height: 72px;

                    margin-bottom: 15px;

                    border-radius: 21px;

                    background: #edf4ff;
                    color: #2563eb;

                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .empty-state h3 {
                    margin: 0;

                    font-size: 15px;
                    font-weight: 800;
                }

                .empty-state p {
                    margin: 6px 0 16px;

                    color: #929aaa;
                    font-size: 11px;
                }

                .empty-add-button {
                    height: 40px;

                    padding: 0 18px;

                    border: 0;
                    border-radius: 10px;

                    background: #2563eb;
                    color: #fff;

                    font-family: inherit;
                    font-size: 11px;
                    font-weight: 700;
                }

                /* =========================
                   SMALL PHONES
                ========================= */

                @media (max-width: 360px) {

                    .classrooms-header {
                        padding: 0 10px;
                    }

                    .classrooms-content {
                        padding-left: 9px;
                        padding-right: 9px;
                    }

                    .add-class-button span {
                        display: none;
                    }

                    .add-class-button {
                        width: 40px;
                        padding: 0;
                    }

                    .classroom-card {
                        padding: 12px;
                    }

                    .classroom-name {
                        font-size: 15px;
                    }

                    .statistics {
                        gap: 6px;
                    }
                }

                /* =========================
                   DESKTOP
                ========================= */

                @media (min-width: 768px) {

                    .classrooms-header {
                        padding: 0 24px;
                    }

                    .classrooms-content {
                        padding-top: 24px;
                    }

                    .classroom-card:hover {
                        transform: translateY(-2px);

                        border-color: #dce6fa;

                        box-shadow:
                            0 8px 25px rgba(20, 35, 60, .07);
                    }

                    .classroom-card:hover
                    .classroom-footer {
                        color: #2563eb;
                    }

                }

            `}</style>
        </div>
    );
};

export default ClassRooms;